import { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { runSync, runPoll, getLastPollError, isServerUnavailableError } from './SyncEngine'
import { logoutIfSessionInvalid, setSnackbar } from '../state/actions'
import { getSessionExpiry } from '../api/api'

/**
 * Intervalo base del poll en segundo plano (ms).
 *
 * 60 s: una visita registrada en otro kiosko aparece en este en alrededor de
 * un minuto (hasta ~72 s con el jitter). Antes era 30 s, y cada kiosko y cada
 * pestaña consultaba `/invitations/batch` y `/visits/batch` dos veces por
 * minuto; eso saturaba el backend (ver docs/local-first.md §5). Es decisión de
 * negocio: si hace falta más inmediatez, se baja aquí.
 */
export const POLL_INTERVAL_MS = 60_000

/**
 * Variación aleatoria de cada espera (±20 %), para que los kioskos que
 * arrancaron juntos (p. ej. tras un corte de luz) no consulten en el mismo
 * segundo.
 */
export const POLL_JITTER_RATIO = 0.2

/**
 * Tope del backoff: ante un 5xx o timeout el intervalo se duplica hasta este
 * valor. Cualquier otro resultado (éxito, sin red, 4xx, ciclo omitido por el
 * lock) lo devuelve a `POLL_INTERVAL_MS`.
 */
export const MAX_POLL_BACKOFF_MS = 5 * 60 * 1000

/**
 * En una reconexión (offline → online) se hace `runSync` completo como mucho
 * una vez en este lapso; dentro de él basta `runPoll`, que igual sube lo
 * pendiente. Una red inestable que cae y vuelve cada pocos segundos
 * disparaba antes un `runSync` por cada vuelta. El arranque en frío no se
 * limita.
 */
export const RECONNECT_FULL_SYNC_MIN_INTERVAL_MS = 5 * 60 * 1000

/**
 * Aplica el jitter de `POLL_JITTER_RATIO` a `baseMs`.
 *
 * @param {number} baseMs
 * @param {() => number} [random=Math.random]
 * @returns {number}
 */
export function withJitter(baseMs, random = Math.random) {
  return Math.round(baseMs * (1 + (random() * 2 - 1) * POLL_JITTER_RATIO))
}

/**
 * Con cuánta antelación se avisa del vencimiento de la sesión.
 *
 * El backend da 14 días fijos y congela el `expiry` en el login, así que el
 * vencimiento llega aunque el kiosko se use a diario. 24 h cubre de sobra un
 * turno de recepción, de modo que quien esté operando lo ve y puede volver a
 * entrar en un momento tranquilo en vez de perder la sesión a media visita.
 */
const EXPIRY_WARNING_THRESHOLD_MS = 24 * 60 * 60 * 1000

/**
 * Cada cuánto se revisa el `expiry` almacenado. Es una comparación local
 * contra el reloj, sin red, así que puede ser holgado: con el umbral de 24 h
 * no hay diferencia práctica entre revisar cada 5 minutos o cada 30 segundos.
 */
const EXPIRY_CHECK_INTERVAL_MS = 5 * 60 * 1000

/**
 * Inner hook that drives the sync engine lifecycle.
 *
 * ### Event-driven syncs (push + pull)
 *  1. **Cold start** — the app loads while already online.
 *  2. **Reconnection** — the app transitions from offline → online. Full
 *     `runSync` at most once per `RECONNECT_FULL_SYNC_MIN_INTERVAL_MS`;
 *     otherwise `runPoll`.
 *
 * ### Background polling
 *  3. **Timer** — `setTimeout` encadenado cada `POLL_INTERVAL_MS` ±20 % while
 *     the tab is online and visible, so visits registered on *other*
 *     terminals in the lobby appear on this screen without any user
 *     interaction. Toma el lock `lobby-sync` con `ifAvailable`: si otra
 *     pestaña está sincronizando, ese ciclo se salta. Ante un 5xx o timeout
 *     el intervalo se duplica (tope `MAX_POLL_BACKOFF_MS`).
 *  4. **Tab becomes visible** — immediate poll when the user returns to the
 *     tab (or the kiosk screen wakes up after being idle), so the list is
 *     always fresh the moment someone looks at it.
 *
 * `runPoll` is pull-first, but it also retries locally-pending visits and
 * invitations. Offline creates never call `runSync` at save time, and a
 * failed push on reconnect would otherwise sit in Dexie until the next
 * full sync (reload/reconnect). The empty-queue path is a single Dexie
 * query, so the extra work is a no-op when nothing is pending.
 *
 * ### Auth gate
 * Both mechanisms are gated on `isAuthenticated` (`!!profile.email`, the same
 * check `RequireAuth` uses in `routes.js`). A logged-out session has no valid
 * tokens, so calling the backend would only produce 401s and waste rate-limit
 * quota — this hook mounts globally in `index.js` regardless of route, so
 * without this gate it would still fire on `/login` or right after logout.
 *
 * ### Auth-expired listener
 * `SyncEngine.js` fires a `lobby:auth-expired` `window` event once a full
 * push/pull cycle finishes having encountered a 401 (see `authExpiredThisCycle`
 * / `flushAuthExpiredFlag` there — by that point every Dexie write for the
 * cycle has already settled, so it's safe to run the destructive logout).
 * This hook listens for it and goes through `logoutIfSessionInvalid()` with no
 * `navigate` — this component is mounted outside `<BrowserRouter>` (see
 * `src/index.js`), so there's no router context here; `RequireAuth` in
 * `routes.js` redirects to `/login` reactively once `profile.email` clears.
 *
 * ### Aviso de vencimiento
 * Revisa el `expiry` guardado y avisa antes de que el token muera, para que el
 * relogin no caiga a media operación. Es solo un aviso: el reloj del kiosko
 * puede estar desfasado, así que nunca cierra la sesión por su cuenta — esa
 * autoridad es del servidor.
 *
 * @param {{ isOnline: boolean, isAuthenticated: boolean, enabled_visit_host: boolean, logoutIfSessionInvalid: Function, setSnackbar: Function }} props
 */
function useSyncEngineInner({
  isOnline,
  isAuthenticated,
  enabled_visit_host,
  logoutIfSessionInvalid,
  setSnackbar
}) {
  const { t } = useTranslation()
  // null = first render (cold start), true/false = known previous state
  const prevOnlineRef = useRef(null)

  // ── 0. Auth-expired listener (background 401 → confirmar → logout) ────────
  // Guards against acting more than once per expiration: multiple concurrent
  // requests within the same `Promise.all` (pushPendingVisits) can each hit a
  // 401, but `flushAuthExpiredFlag` only dispatches ONE event per cycle. This
  // ref is a second line of defense in case a stray poll manages to fire
  // another event before the in-flight logout has finished tearing down the
  // session (localStorage/Dexie clear + CLEAN_AUTH).
  const loggingOutRef = useRef(false)

  useEffect(() => {
    const onAuthExpired = () => {
      if (loggingOutRef.current) return
      loggingOutRef.current = true
      // `logoutIfSessionInvalid` confirma contra el servidor antes de destruir
      // nada. Este camino corre cada minuto sin que nadie toque el kiosko, así
      // que deslogear ante el primer 401 —como se hacía antes— convertía
      // cualquier hipo del backend en un cierre de sesión en plena operación.
      Promise.resolve(logoutIfSessionInvalid()).finally(() => {
        loggingOutRef.current = false
      })
    }

    window.addEventListener('lobby:auth-expired', onAuthExpired)
    return () => window.removeEventListener('lobby:auth-expired', onAuthExpired)
  }, [logoutIfSessionInvalid])

  // ── 0.b Aviso anticipado de vencimiento ───────────────────────────────────
  // Guarda el `expiry` ya avisado en vez de un booleano: así un relogin (que
  // trae un `expiry` nuevo) vuelve a armar el aviso, y mientras siga siendo la
  // misma sesión no se repite en cada intervalo.
  const warnedExpiryRef = useRef(null)

  useEffect(() => {
    if (!isAuthenticated) {
      warnedExpiryRef.current = null
      return
    }

    const checkExpiry = () => {
      const expiresAt = getSessionExpiry()
      if (!expiresAt) return

      const remainingMs = expiresAt - Date.now()
      // Ya venció: no hay nada que anticipar, el 401 llegará solo y el camino
      // del listener se encarga.
      if (remainingMs <= 0) return
      if (remainingMs > EXPIRY_WARNING_THRESHOLD_MS) return
      if (warnedExpiryRef.current === expiresAt) return

      warnedExpiryRef.current = expiresAt
      setSnackbar({
        open: true,
        severity: 'warning',
        // Sin auto-cierre: es un aviso accionable, no una notificación de paso.
        autoHideDuration: null,
        message: t('session expires soon', {
          hours: Math.max(1, Math.round(remainingMs / 3_600_000))
        })
      })
    }

    checkExpiry()
    const intervalId = setInterval(checkExpiry, EXPIRY_CHECK_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [isAuthenticated, setSnackbar, t])

  // ── 1 & 2. Event-driven full sync (push + pull) ───────────────────────────
  // Cuándo empezó el último `runSync` lanzado desde aquí (ver
  // `RECONNECT_FULL_SYNC_MIN_INTERVAL_MS`).
  const lastFullSyncAtRef = useRef(null)

  useEffect(() => {
    const isColdStart = prevOnlineRef.current === null
    const wasOffline = prevOnlineRef.current === false
    const isNowOnline = isOnline === true

    // Capture cold-start/reconnect BEFORE writing the ref. Updating first
    // made `prevOnlineRef.current === null` always false, so a reload while
    // already online never pushed pending visits.
    prevOnlineRef.current = isOnline

    if (!isAuthenticated) return
    if (!isNowOnline || !(wasOffline || isColdStart)) return

    const syncEmployees = !!enabled_visit_host
    const lastFullSyncAt = lastFullSyncAtRef.current
    const fullSyncAllowed = isColdStart
      || lastFullSyncAt == null
      || Date.now() - lastFullSyncAt >= RECONNECT_FULL_SYNC_MIN_INTERVAL_MS

    if (fullSyncAllowed) {
      lastFullSyncAtRef.current = Date.now()
      runSync({ syncEmployees })
        .catch(err => console.error('[useSyncEngine]', err))
    } else {
      // Reconexión dentro de la ventana: igual hay que subir lo que se guardó
      // offline, pero sin repetir el pull completo. Espera el lock.
      runPoll({ syncEmployees })
        .catch(err => console.warn('[useSyncEngine] poll error:', err))
    }
  // Re-run whenever the online status or auth state changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, isAuthenticated])

  // ── 3 & 4. Background polling ─────────────────────────────────────────────
  useEffect(() => {
    // Nothing to do while offline or logged out — skip entirely. The
    // reconnection handler (above) does a full sync once connectivity AND
    // auth are both restored.
    if (!isOnline || !isAuthenticated) return

    let cancelled = false
    let timeoutId = null
    // Intervalo vigente: `POLL_INTERVAL_MS`, o más mientras dure el backoff.
    let intervalMs = POLL_INTERVAL_MS

    const poll = async () => {
      // Skip if the tab is hidden (saves resources on kiosk screens that may
      // sit idle for long periods). The visibilitychange handler below will
      // fire an immediate poll when the tab becomes visible again.
      if (document.visibilityState === 'hidden') return
      try {
        const ok = await runPoll({ syncEmployees: !!enabled_visit_host, ifAvailable: true })
        // Una sola regla: servidor no disponible (5xx/timeout) → duplicar;
        // cualquier otro resultado (éxito, sin red, 4xx, ciclo omitido por el
        // lock de otra pestaña) → intervalo normal.
        intervalMs = !ok && isServerUnavailableError(getLastPollError())
          ? Math.min(intervalMs * 2, MAX_POLL_BACKOFF_MS)
          : POLL_INTERVAL_MS
      } catch (err) {
        console.warn('[useSyncEngine] poll error:', err)
        intervalMs = POLL_INTERVAL_MS
      }
    }

    // `setTimeout` encadenado en vez de `setInterval`: cada espera lleva su
    // propio jitter y respeta el backoff, y nunca se apila un poll sobre otro.
    const scheduleNext = () => {
      timeoutId = setTimeout(async () => {
        await poll()
        if (!cancelled) scheduleNext()
      }, withJitter(intervalMs))
    }

    // Immediate poll on tab/window becoming visible again
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') poll()
    }

    scheduleNext()
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  // Restart the timer when connectivity, auth, or the visit-host flag changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, isAuthenticated, enabled_visit_host])
}

/**
 * Null-rendering React component that mounts the sync engine inside the
 * Redux Provider tree. Mounting it here (rather than in a page component)
 * guarantees the engine is active for the entire app session — even when
 * the user is not on the Visits page.
 *
 * Rendered once in `src/index.js` next to `NetworkDetector`.
 */
function SyncEngineComponent({
  isOnline,
  isAuthenticated,
  enabled_visit_host,
  logoutIfSessionInvalid,
  setSnackbar
}) {
  useSyncEngineInner({
    isOnline,
    isAuthenticated,
    enabled_visit_host,
    logoutIfSessionInvalid,
    setSnackbar
  })
  return null
}

const mapStateToProps = state => ({
  isOnline: state.utilities.isOnline,
  isAuthenticated: !!state.profile.email,
  enabled_visit_host: state.profile.enabled_visit_host,
})

const mapDispatchToProps = {
  logoutIfSessionInvalid,
  setSnackbar,
}

export default connect(mapStateToProps, mapDispatchToProps)(SyncEngineComponent)
